/**
 * Password sign-in. The first login on a device, and the way back after a revoke.
 *
 * Note there is no `useEffect` mirroring `isLoading` or the error into local state: the mutation
 * hook already exposes both, and copying them would put the UI one render behind the truth.
 */

import { useState, type SyntheticEvent } from 'react';
import { useLocation, useNavigate } from 'react-router';

import { ErrorCode, isApiError, signedIn, useLoginMutation } from '@pos/shared';

import { useAppDispatch } from '@/app/hooks';
import { Button, Field, InlineError } from '@/shared/ui';

type LocationState = { from?: string } | null;

export function PasswordLoginForm({ username: initialUsername = '' }: { username?: string }) {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const location = useLocation();

  const [login, { isLoading, error }] = useLoginMutation();
  const [username, setUsername] = useState(initialUsername);
  const [password, setPassword] = useState('');
  const [revealed, setRevealed] = useState(false);

  const submit = async (event: SyntheticEvent<HTMLFormElement>) => {
    event.preventDefault();

    try {
      // `unwrap()` rather than inspecting the result union: the union's `data` is optional, so
      // `'data' in result` compiles and still hands you `Session | undefined`.
      const session = await login({ username: username.trim(), password }).unwrap();

      dispatch(signedIn(session));
      const from = (location.state as LocationState)?.from;
      void navigate(from ?? '/sell', { replace: true });
    } catch {
      // The mutation hook already holds the error and the form renders it below.
    }
  };

  return (
    <form onSubmit={(event) => void submit(event)} className="flex flex-col gap-5">
      <Field
        label="Username"
        size="lg"
        value={username}
        autoComplete="username"
        autoCapitalize="none"
        autoCorrect="off"
        spellCheck={false}
        required
        onChange={(event) => {
          setUsername(event.target.value);
        }}
      />

      <Field
        label="Password"
        size="lg"
        type={revealed ? 'text' : 'password'}
        value={password}
        autoComplete="current-password"
        required
        onChange={(event) => {
          setPassword(event.target.value);
        }}
        trailing={
          <button
            type="button"
            aria-label={revealed ? 'Hide password' : 'Show password'}
            aria-pressed={revealed}
            onClick={() => {
              setRevealed((value) => !value);
            }}
            // 44px square: a thumb target beside a field, not a decoration. `-mr-2` pulls it back
            // into the field's own padding so the icon sits where the eye expects it.
            className="-mr-2 flex size-compact shrink-0 items-center justify-center rounded-md text-content-muted transition-colors duration-fast ease-standard hover:text-content-primary"
          >
            <EyeIcon open={revealed} />
          </button>
        }
      />

      {error !== undefined && <InlineError message={messageFor(error)} />}

      <Button
        type="submit"
        size="lg"
        fullWidth
        loading={isLoading}
        disabled={username.trim().length === 0 || password.length === 0}
      >
        Sign in
      </Button>

      <p className="flex items-start gap-3 rounded-md bg-surface-sunken px-4 py-3 text-sm leading-snug text-content-secondary">
        <InfoIcon />
        <span>Signing in here signs this account out on any other device.</span>
      </p>
    </form>
  );
}

/**
 * Every branch says what happened and what to do next. The API's own message is used where it is
 * already actionable; these are the cases where it is not, or where the client knows more.
 */
function messageFor(error: unknown): string {
  if (!isApiError(error)) return 'Sign-in failed. Check the connection and try again.';

  switch (error.code) {
    case ErrorCode.InvalidCredentials:
      return 'That username or password is not right. Check both and try again.';
    case ErrorCode.AccountDeactivated:
      return 'This account has been switched off. Ask the owner to turn it back on.';
    case ErrorCode.Offline:
      return 'No connection to the server. Sign-in needs the network — the offline catalogue cannot verify a password.';
    default:
      return error.message;
  }
}

/** Drawn inline at the nav icons' stroke weight, as every glyph in the till is. */
function EyeIcon({ open }: { open: boolean }) {
  return (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path
        d="M2.5 12S6 5.5 12 5.5 21.5 12 21.5 12 18 18.5 12 18.5 2.5 12 2.5 12Z"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinejoin="round"
      />
      <circle cx="12" cy="12" r="3" stroke="currentColor" strokeWidth="1.8" />
      {!open && <path d="M4 20 20 4" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />}
    </svg>
  );
}

function InfoIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden="true" className="mt-px shrink-0">
      <circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="1.8" />
      <path d="M12 11v5.5M12 7.5v.5" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
    </svg>
  );
}
